import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';

import PaginatedTable from '../common/containers/PaginatedTable';

import Permissions from '../auth/Permissions';

import { getIdMap, safeExtractFromObject } from '../helpers/helpers';

import getColumns from './materialTableColumns';

import { formatEquipmentLabelFromCompanySettings } from '../equipment/equipment.helpers';

import { getCostcodeByIds } from '../costcodes/state/costcodes.actions';

const TABLE_OFFSET = 270;
const TABLE_HEADER_HEIGHT = 60;

export default ({
  onFolderEdit,
  onRowClick,
  onSelect,
  data,
  selectedColumns,
  selected,
  onShare,
  setStatusFilters,
  initialSortFilters,
}) => {
  const dispatch = useDispatch();
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const costcodeIdMap = useSelector(state => state.costcodes.costcodeIdMap);
  const equipment = useSelector(state => state.equipment.equipment);
  const materials = useSelector(state => state.materials.materials);
  const globalMaterialLocations = useSelector(state => state.globalMaterialLocations.globalMaterialLocations);
  const vendors = useSelector((state) => state.vendors.vendors);

  const [filteredData, setFilteredData] = useState(data);

  const equipmentMap = useMemo(() => getIdMap(equipment),[equipment]);
  const globalMaterialLocationsMap = useMemo(() => getIdMap(globalMaterialLocations), [globalMaterialLocations]);
  const vendorMap = useMemo(() => vendors, [vendors]);
  const selectedRowKeys = useMemo(() => selected.filter((s) => !!s).map((s) => s.id),[selected]);


  const costcodeIds = useMemo(() => (
    Array.from(new Set(data.map(({ costcodeId }) => costcodeId)))
      .filter(Boolean)
  ), [data]);

  const onSelectRow = useCallback((item, checked) => {
    if (checked) {
      onSelect(selected.concat([item]))
    } else {
      onSelect(selected.filter((i) => i.id !== item.id))
    }
  },[onSelect, selected, selectedRowKeys]);
  const onSelectAll = useCallback((checked) => {
    onSelect(checked ? filteredData : []);
  },[filteredData]);

  const uniqueLocations = useMemo(() => {
    const locationMap = {};

    Object.values(materials).forEach(({ locations = [] }) => {
      locations.forEach(({ globalMaterialLocationsId, equipmentId }) => {
        const {
          [equipmentId]: {
            name,
            code,
          } = {},
        } = equipmentMap;
        const equipmentLabel = formatEquipmentLabelFromCompanySettings({
          name,
          code,
          settings,
        });
        const {
          [globalMaterialLocationsId]: {
            locationText: globalMaterialLocationsText,
          } = {},
        } = globalMaterialLocationsMap;
        const realName = equipmentLabel ?? globalMaterialLocationsText;
        if(!(realName in locationMap)) {
          locationMap[realName] = {
            text: realName,
             value: equipmentId || globalMaterialLocationsId
            };
        }
      });
    });

    return locationMap;
  },[materials, equipmentMap, globalMaterialLocationsMap, settings]);

  const columns = useMemo(() => (
    getColumns({
      costcodeIdMap,
      onFolderEdit,
      selectedColumns,
      equipmentMap,
      globalMaterialLocationsMap,
      uniqueLocations,
      onShare,
      settings,
      vendorMap,
    })
  ), [costcodeIdMap, onFolderEdit, selectedColumns, equipmentMap, uniqueLocations, settings, vendorMap]);

  useEffect(() => {
    if (selected.length) {
      const filteredIdSet = new Set(
        filteredData.map((data) => data.id)
      );
      const newSelected = selected.filter((s) => filteredIdSet.has(s.id));
      if (newSelected.length !== selected.length) onSelect(newSelected);
    }
  }, [filteredData, selected]);

  const onHeaderRow = useCallback(() => {
    if (!settings?.uxRedesignPt1) return null;
    return {
      className: 'ontraccr-table-header',
    };
  }, [settings]);


  useEffect(() => {
    if (costcodeIds.length && (!selectedColumns || selectedColumns.has('costcode'))) {
      dispatch(getCostcodeByIds(costcodeIds));
    }
  }, [costcodeIds, selectedColumns]);

  return (
    <PaginatedTable
      scroll={{ x: 'max-content' }}
      scrollOffset={TABLE_OFFSET + TABLE_HEADER_HEIGHT}
      size='small'
      rowKey='id'
      columns={columns}
      dataSource={data}
      initialSortFilters={initialSortFilters}
      rowClassName={(record) => (
        (record.quantity && record.quantity <= record.quantityThreshold) ?
        'materials-table-row-threshold' :
        'materials-table-row'
      )}
      onRow={(record) => ({
        onClick:() => onRowClick(record)
      })}
      rowSelection={Permissions.hasWrite('MATERIALS') ? {
        fixed:true,
        onSelect: onSelectRow,
        onSelectAll,
        selectedRowKeys,
        getCheckboxProps: () => ({
          className: settings?.uxRedesignPt1 ? 'ontraccr-checkbox' : '',
        }),
      } : null}
      onFilteredDataChanged={setFilteredData}
      onHeaderRow={onHeaderRow}
      setStatusFilters={setStatusFilters}
      containerStyle={{
        height: `calc(100vh - ${TABLE_OFFSET}px)`,
      }}
    />
  );
}
